import React, { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import {
  useStartAssessmentQuery,
  deleteAnswer,
  answerAssessment,
  submitAnswers,
} from "..";
import { GET_ASSESSMENT_LIST } from "modules";
import { useImmer } from "use-immer";
import { errorFormatter, useNetworkStatus, useMediaPreview } from "shared";

const initInstruction = {
  noOfQuestions: "",
  maxTime: "",
  totalMark: "",
  maxAttempt: "",
  instructions: "",
  name: "",
};

export const useQuestionnaire = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOnline } = useNetworkStatus();
  const mediaPreviewState = useMediaPreview();

  const assessmentId = JSON.parse(sessionStorage.getItem("assessmentId"));
  const slotId = JSON.parse(sessionStorage.getItem("slotId"));
  const attemptId = JSON.parse(sessionStorage.getItem("attemptId"));
  const enabled =
    isOnline &&
    assessmentId != undefined &&
    slotId != undefined &&
    (location.pathname === "/home/assessment/start" ||
      location.pathname === "/home/assessment/review");
  const assessmentLocalKey = `assessmentData_${assessmentId}`;

  const [allAnsweredQuestions, setAllAnsweredQuestions] = useImmer([]);
  const [questionnaire, setQuestionnaire] = useImmer(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useImmer({
    sectionIndex: -1,
    questionIndex: -1,
  });
  const [isModalOpen, setIsModalOpen] = useImmer({
    type: "",
    isOpen: false,
    actionFrom: "",
  });
  const [currentQuestion, setCurrentQuestion] = useImmer(null);
  const [markedQuestions, setMarkedQuestions] = useImmer([]);
  const [instruction, setInstruction] = useImmer(initInstruction);
  const [help, setHelp] = useImmer("");
  const [isTimeout, setIsTimeout] = useImmer(false);
  const [answerPayload, setAnswerPayload] = useImmer(null);

  const startAssessmentQuery = useStartAssessmentQuery({
    enabled,
    assessmentId,
    slotId,
    attemptId,
  });

  const errorDetails =
    startAssessmentQuery && startAssessmentQuery?.error?.response;

  const selectedProgram = JSON.parse(localStorage.getItem("selectedProgram"));
  const programId = selectedProgram?.id;

  useEffect(() => {
    if (startAssessmentQuery?.error && errorDetails?.status === 400) {
      navigate("/home/assessment/expired", {
        state: {
          message: errorDetails?.data?.message,
          status: errorDetails?.status,
        },
        replace: true,
      });
    }
  }, [startAssessmentQuery?.error]);

  useEffect(() => {
    if (startAssessmentQuery && startAssessmentQuery?.data) {
      const getLocalAssessmentData = sessionStorage.getItem(assessmentLocalKey)
        ? JSON.parse(sessionStorage.getItem(assessmentLocalKey))
        : null;

      let formattedQuestions = [];
      if (!getLocalAssessmentData) {
        formattedQuestions = startAssessmentQuery?.data?.questions?.map(
          (q, i) => {
            return {
              ...q,
              questionNumber: i + 1,
            };
          }
        );
      }

      const qst =
        getLocalAssessmentData === null
          ? {
              ...startAssessmentQuery?.data,
              questions: formattedQuestions,
            }
          : {
              ...getLocalAssessmentData,
            };

      setQuestionnaire((draft) => {
        draft = qst;
        return draft;
      });

      const index = sessionStorage.getItem(
        `currentQuestionIndex_${assessmentId}`
      )
        ? JSON.parse(
            sessionStorage.getItem(`currentQuestionIndex_${assessmentId}`)
          )
        : {
            sectionIndex: 0,
            questionIndex: 0,
          };
      setCurrentQuestionIndex((draft) => {
        draft = index;
        return draft;
      });
    }
  }, [startAssessmentQuery.data]);

  useEffect(() => {
    if (questionnaire !== null) {
      sessionStorage.setItem(assessmentLocalKey, JSON.stringify(questionnaire));
      setAnswerPayload((draft) => {
        draft = null;
        return draft;
      });
      setCurrentQuestion((draft) => {
        draft = questionnaire?.questions[currentQuestionIndex.questionIndex];
        return draft;
      });
    }
  }, [questionnaire]);

  useEffect(() => {
    if (!isOnline) {
      const index = sessionStorage.getItem(
        `currentQuestionIndex_${assessmentId}`
      )
        ? JSON.parse(
            sessionStorage.getItem(`currentQuestionIndex_${assessmentId}`)
          )
        : {
            sectionIndex: 0,
            questionIndex: 0,
          };

      setCurrentQuestionIndex((draft) => {
        draft = index;
        return draft;
      });

      const getLocalAssessmentData = sessionStorage.getItem(assessmentLocalKey)
        ? JSON.parse(sessionStorage.getItem(assessmentLocalKey))
        : null;

      if (getLocalAssessmentData) {
        setQuestionnaire((draft) => {
          draft = getLocalAssessmentData;
          return draft;
        });
      }
    }
  }, []);

  useEffect(() => {
    if (questionnaire !== null) {
      sessionStorage.setItem(
        `currentQuestionIndex_${assessmentId}`,
        JSON.stringify(currentQuestionIndex)
      );
      setCurrentQuestion((draft) => {
        draft = questionnaire?.questions[currentQuestionIndex.questionIndex];
        return draft;
      });
    }
  }, [currentQuestionIndex]);

  useEffect(() => {
    const sectionIndex = getCurrentSectionIndex(currentQuestion?.sectionId);
    setCurrentQuestionIndex((draft) => {
      draft.sectionIndex = sectionIndex;
      return draft;
    });
  }, [currentQuestion]);

  useEffect(() => {
    sessionStorage.setItem("markedQuestions", JSON.stringify(markedQuestions));
  }, [markedQuestions]);

  const localMarkedQuestions = sessionStorage.getItem("markedQuestions")
    ? JSON.parse(sessionStorage.getItem("markedQuestions"))
    : [];

  useEffect(() => {
    setMarkedQuestions((draft) => {
      draft = localMarkedQuestions;
      return draft;
    });
  }, []);

  useEffect(() => {
    const filteredAnswer =
      questionnaire &&
      questionnaire?.questions.filter((q) => {
        return q.answer !== null;
      });

    const answers = filteredAnswer
      ? filteredAnswer.map((q) => {
          const {
            id = 0,
            assessmentMasterId,
            questionId,
            choiceId,
            sectionId,
          } = q.answer;
          return {
            id,
            assessmentMasterId,
            questionId,
            choiceId,
            sectionId,
          };
        })
      : [];

    setAllAnsweredQuestions((draft) => {
      draft = [...answers];
      return draft;
    });
  }, [questionnaire]);

  const answeringQuestion = useMutation(answerAssessment, {
    onError: (e) => {
      errorFormatter(e);
    },
    onSuccess: (e) => {
      setQuestionnaire((draft) => {
        const question = draft?.questions?.find((q) => {
          return q.questionId === e.questionId && q.sectionId === e.sectionId;
        });
        question.answer = e;
        return draft;
      });
    },
    onSettled: () => {
      setAnswerPayload((draft) => {
        draft = null;
        return draft;
      });
    },
  });

  useEffect(() => {
    if (answerPayload) {
      setQuestionnaire((draft) => {
        let question = draft?.questions?.find((q) => {
          return (
            q?.questionId === answerPayload?.questionId &&
            q?.sectionId === answerPayload?.sectionId
          );
        });
        question.answer = answerPayload;
      });
      if (isOnline) {
        answeringQuestion.mutate(answerPayload);
      }
    }
  }, [answerPayload]);

  const clearAnswer = useMutation(deleteAnswer, {
    onError: (e) => {},
    onSuccess: (e) => {
      setQuestionnaire((draft) => {
        const question = draft?.questions?.find((q) => {
          return (
            q?.questionId === e?.questionId && q?.sectionId === e?.sectionId
          );
        });
        question.answer = null;
        return draft;
      });
    },
    onSettled: (e) => {
      setQuestionnaire((draft) => {
        const question = draft?.questions?.find((q) => {
          return (
            q?.questionId === e?.questionId && q?.sectionId === e?.sectionId
          );
        });
        question.answer = null;
        return draft;
      });
    },
  });

  const submitAllAnswers = useMutation(submitAnswers, {
    onError: (e) => {
      errorFormatter(e);
    },
    onSuccess: (e) => {
      setIsModalOpen((draft) => {
        draft.type = "";
        draft.isOpen = false;
        return draft;
      });

      queryClient.invalidateQueries([GET_ASSESSMENT_LIST, programId]);
      navigate("/home/assessment/result", {
        replace: true,
      });
    },
    onSettled: () => {},
  });

  const getCurrentQuestionIndex = (questionNumber) => {
    const currentQuestonIndex = questionnaire?.questions.findIndex((q) => {
      return q.questionNumber === questionNumber;
    });
    return currentQuestonIndex;
  };

  const getCurrentSectionIndex = (sectionId) => {
    const currentSectionIndex = questionnaire?.sections.findIndex((s) => {
      return s.sectionId === sectionId;
    });
    return currentSectionIndex;
  };

  const onChangeSection = useCallback(
    (sectionId) => {
      const sectionIndex = getCurrentSectionIndex(sectionId);
      const filteredQuestions = getFilteredQuestions(sectionIndex);
      const firstQuestionIndex = questionnaire?.questions.indexOf(
        filteredQuestions[0]
      );
      setCurrentQuestionIndex((draft) => {
        draft = {
          sectionIndex,
          questionIndex: firstQuestionIndex,
        };
        return draft;
      });
    },
    [currentQuestionIndex]
  );

  const onChangeSidebarButton = useCallback(
    (sectionId, questionIndex) => {
      const sectionIndex = getCurrentSectionIndex(sectionId);
      setCurrentQuestionIndex((draft) => {
        draft = {
          sectionIndex,
          questionIndex,
        };
        return draft;
      });
    },
    [currentQuestionIndex]
  );

  const onAnswer = ({ questionId, choiceId, sectionId }) => {
    if (questionId && choiceId) {
      const { assessmentMasterId, attemptId } = questionnaire && questionnaire;

      setAnswerPayload((draft) => {
        draft = {
          assessmentMasterId,
          questionId,
          choiceId,
          attemptId,
          sectionId,
        };
        return draft;
      });
    }
  };

  const onClearAnswer = (
    answerId = null,
    questionId = null,
    sectionId = null
  ) => {
    if (isOnline && answerId) {
      clearAnswer.mutate(answerId);
    } else {
      if (questionId && sectionId) {
        setQuestionnaire((draft) => {
          let question = draft?.questions?.find((q) => {
            return q?.questionId === questionId && q?.sectionId === sectionId;
          });
          question.answer = null;
        });
      }
    }
  };

  const submitAnswersList = () => {
    const getLocalAssessmentData = sessionStorage.getItem(assessmentLocalKey)
      ? JSON.parse(sessionStorage.getItem(assessmentLocalKey))
      : null;

    sessionStorage.setItem(
      `assessmentMasterId_${assessmentId}`,
      getLocalAssessmentData?.assessmentMasterId
    );

    submitAllAnswers.mutate({
      assessmentMasterId: getLocalAssessmentData?.assessmentMasterId,
      payload: allAnsweredQuestions,
    });
  };

  const onSubmitAnswer = () => {
    if (isOnline) {
      submitAnswersList();
    } else {
      alert("You are offline now!. Please check your internet connection.");
    }
  };

  const getFilteredQuestions = (sectionIndex) => {
    let filteredQuestions = [];
    if (questionnaire) {
      const currentSection = questionnaire?.sections[sectionIndex];
      filteredQuestions = questionnaire?.questions.filter(
        (q) => q.sectionId === currentSection?.sectionId
      );
    }
    return filteredQuestions;
  };

  const onNext = useCallback(() => {
    setCurrentQuestionIndex((draft) => {
      draft.questionIndex = draft.questionIndex + 1;
      return draft;
    });
  }, [currentQuestionIndex]);

  const onPrevious = useCallback(() => {
    setCurrentQuestionIndex((draft) => {
      draft.questionIndex = draft.questionIndex - 1;
      return draft;
    });
  }, [currentQuestionIndex]);

  const onMarkQuestion = useCallback(
    (question) => {
      const existingItem = markedQuestions.find(
        (q) =>
          q?.questionId === question?.questionId &&
          q?.sectionId === question?.sectionId
      );
      if (existingItem) {
        const newArray = markedQuestions.filter((q) => {
          return !(
            q?.questionId === existingItem?.questionId &&
            q?.sectionId === existingItem?.sectionId
          );
        });
        setMarkedQuestions((draft) => {
          draft = [...newArray];
          return draft;
        });
      } else {
        setMarkedQuestions((draft) => {
          draft = [...draft, question];
          return draft;
        });
      }
    },
    [markedQuestions]
  );

  const onTimeOut = () => {
    setIsModalOpen((draft) => {
      draft = { type: "timeOut", isOpen: true };
      return draft;
    });
    setIsTimeout((draft) => {
      draft = true;
      return draft;
    });

    setTimeout(() => {
      onSubmitAnswer();
    }, 5000);
    navigate("/home/assessment/review", { replace: true });
  };

  const onFindQuestion = useCallback(
    (sectionId, questionId) => {
      if (questionnaire) {
        const questionIndex = questionnaire?.questions?.findIndex(
          (question) => {
            return (
              question?.questionId === questionId &&
              question?.sectionId === sectionId
            );
          }
        );
        const sectionIndex = questionnaire?.sections?.findIndex((question) => {
          return question?.sectionId === sectionId;
        });
        setCurrentQuestionIndex((draft) => {
          draft = {
            sectionIndex,
            questionIndex,
          };
          return draft;
        });
      }
    },
    [currentQuestionIndex]
  );

  const onToggleInstruction = (instruction = null, from = "") => {
    if (isModalOpen.type === "" && isModalOpen.isOpen === false) {
      if (instruction) {
        setInstruction((draft) => {
          draft = instruction;
          return draft;
        });
      } else {
        const { maxAttempt, noOfQuestions, maxTime, totalMark, instructions } =
          startAssessmentQuery?.data;

        setInstruction((draft) => {
          draft = {
            maxAttempt,
            noOfQuestions,
            maxTime,
            totalMark,
            instructions,
          };
          return draft;
        });
      }

      setIsModalOpen((draft) => {
        draft = {
          type: "instruction",
          isOpen: true,
          from,
        };
        return draft;
      });
    } else {
      setInstruction((draft) => {
        draft = initInstruction;
        return draft;
      });
      setIsModalOpen((draft) => {
        draft = {
          type: "",
          isOpen: false,
          from: "",
        };
        return draft;
      });
    }
  };

  const onToggleHelp = () => {
    if (isModalOpen.type === "" && isModalOpen.isOpen === false) {
      setIsModalOpen((draft) => {
        draft = {
          type: "help",
          isOpen: true,
        };
        return draft;
      });
      setHelp((draft) => {
        draft = startAssessmentQuery?.data?.help;
        return draft;
      });
    } else {
      setHelp((draft) => {
        draft = "";
        return draft;
      });
      setIsModalOpen((draft) => {
        draft = {
          type: "",
          isOpen: false,
        };
        return draft;
      });
    }
  };

  const disableBackButton = () => {
    window.history.pushState(null, null, window.location.href);
    window.addEventListener("popstate", function (e) {
      window.history.pushState(null, null, window.location.href);
    });
  };

  return {
    startAssessmentQuery,
    questionnaire,
    markedQuestions,
    currentQuestionIndex,
    isOnline,
    setCurrentQuestionIndex,
    currentQuestion,
    onChangeSection,
    isModalOpen,
    setIsModalOpen,
    getFilteredQuestions,
    onFindQuestion,
    onAnswer,
    onClearAnswer,
    onNext,
    onPrevious,
    onMarkQuestion,
    onTimeOut,
    instruction,
    setInstruction,
    onToggleInstruction,
    onToggleHelp,
    submitAllAnswers,
    help,
    onChangeSidebarButton,
    getCurrentSectionIndex,
    getCurrentQuestionIndex,
    isTimeout,
    setMarkedQuestions,
    disableBackButton,
    onSubmitAnswer,
    mediaPreviewState,
    answeringQuestion,
    clearAnswer,
  };
};
